import { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { ViewTypes } from 'stores/NotificationsStore'
import Styles from './NotificationsTray.module.scss'
import { OptionsDropdown } from './OptionsDropdown/OptionsDropdown'
import { NotificationItem } from './NotificationItem/NotificationItem'
import { ScrollContainer } from 'components/DocRightRailV2/ScrollContainer/ScrollContainer'
import InfiniteScroll from 'common/InfiniteScroll/InfiniteScroll'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { LoadingState } from './LoadingState/LoadingState'
import { DoNotDisturbState } from './DoNotDisturbState/DoNotDisturbState'
import { EmptyState } from './EmptyState/EmptyState'
import { Button } from 'blaze-ui/Button'
import { useNotificationsQuery } from './hooks/useNotificationsQuery'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Check2 } from 'blaze-ui/icons'
import { Tasks } from './Tasks/Tasks'

interface Props {
  onClose: () => void
  isShowAllReadDisabled: boolean
}

export const UnreadNotifications = observer(({ onClose, isShowAllReadDisabled }: Props) => {
  const { notifications } = useRootStore()
  const { isLoading, hasNextPage, fetchNextPage, isRefetching, isFetchingNextPage } = useNotificationsQuery({
    read: false,
    enabled: !notifications.mutedAt,
  })
  const currentWorkspace = useCurrentWorkspace()

  const showInfiniteScroll = !isLoading && hasNextPage !== undefined

  useEffect(() => {
    if (notifications.mutedAt) return

    // Mark all as seen on component dismount
    return () => {
      if (notifications.unseen.length > 0 && currentWorkspace) notifications.markAllAsSeen(currentWorkspace)
    }
  }, [notifications]) // eslint-disable-line react-hooks/exhaustive-deps

  const markAllAsRead = async () => {
    if (!currentWorkspace) return null

    notifications.markAllAsRead(currentWorkspace)
  }

  if (notifications.mutedAt) {
    return (
      <>
        <div className={Styles.header}>
          <div className={Styles.titleContainer}>
            <Heading level={4}>Notifications</Heading>
            <OptionsDropdown onClose={onClose} />
          </div>
        </div>
        <DoNotDisturbState onClose={onClose} />
      </>
    )
  }

  return (
    <>
      <div className={Styles.header}>
        <div className={Styles.titleContainer}>
          <Heading level={4}>Notifications</Heading>
          <OptionsDropdown onClose={onClose} />
        </div>
        {notifications.unread.length > 0 && (
          <Button size="md" variant="ghost" frontIcon={Check2} onPress={markAllAsRead}>
            Archive All
          </Button>
        )}
      </div>
      <Tasks onClose={onClose} />
      <ScrollContainer className={Styles.scrollContainer}>
        {isLoading || (isRefetching && !isFetchingNextPage) ? (
          <LoadingState />
        ) : (
          <>
            {notifications.unread.map((notification) => (
              <NotificationItem notification={notification} key={notification.id} onClose={onClose} />
            ))}
            {showInfiniteScroll && (
              <InfiniteScroll hasMore={hasNextPage} loadMore={fetchNextPage} className={Styles.infiniteScroll}>
                <LoadingState />
              </InfiniteScroll>
            )}
            {notifications.unread.length === 0 && !hasNextPage && <EmptyState />}
          </>
        )}
      </ScrollContainer>
      <div className={Styles.showArchived}>
        <Button
          size="lg"
          variant="ghost"
          fullWidth
          onPress={() => notifications.setCurrentView(ViewTypes.Read)}
          isDisabled={isShowAllReadDisabled}
        >
          Show All Archived
        </Button>
      </div>
    </>
  )
})
